import { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { supabase } from '../../lib/supabase';
import { formatBRL } from '../../lib/currency';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

type Period = '7d' | '14d' | '30d' | '90d';
const PERIODS: { key: Period; label: string }[] = [
  { key: '7d', label: '7 dias' }, { key: '14d', label: '14 dias' },
  { key: '30d', label: '30 dias' }, { key: '90d', label: '90 dias' },
];
const TABS: { label: string; title?: string }[] = [
  { label: 'Receita' },
  { label: 'Uso' },
  { label: 'Funil', title: 'Funil de conversão: mostra quantos usuários passam por cada etapa, do primeiro acesso até a compra' },
  { label: 'Retenção', title: 'Retenção: % de usuários que continuam usando o app após 1 semana, 1 mês e 3 meses' },
  { label: 'Aquisição', title: 'Aquisição: origem dos novos usuários, indicação, afiliado, campanha' },
  { label: 'Astro Coach' },
  { label: 'Demográficos', title: 'Perfil dos usuários: signos, gênero, faixa etária e localização por IP' },
];
const COLORS = ['#F5C842', '#a78bfa', '#4ade80', '#60a5fa', '#fb923c', '#f472b6'];

interface CohortRow {
  cohort: string;
  users: number;
  week1: number | null;
  week4: number | null;
  month3: number | null;
}

export function AdminAnalyticsPage() {
  const [period,    setPeriod]    = useState<Period>('30d');
  const [tab,       setTab]       = useState(0);
  const [revenue,   setRevenue]   = useState<{ name: string; valor: number }[]>([]);
  const [plans,     setPlans]     = useState<{ name: string; value: number }[]>([]);
  const [usageData, setUsageData] = useState<{ feature: string; count: number; feedback: number }[]>([]);
  const [funnel,    setFunnel]    = useState<{ step: string; value: number; rate: string }[]>([]);
  const [loading,   setLoading]   = useState(false);
  const [retention, setRetention] = useState<{ cohorts: CohortRow[]; insufficient: boolean; reason?: string } | null>(null);
  const [retentionLoading, setRetentionLoading] = useState(false);
  const [referrals, setReferrals] = useState<{ source: string; count: number; converted: number }[]>([]);
  const [coachStats, setCoachStats] = useState<{ active: number; completed: number; avgWeek: number; byPlan: { plan: string; count: number }[] } | null>(null);

  // Demographics
  interface DemographicsData {
    signs: { name: string; value: number }[];
    genders: { name: string; value: number }[];
    ageGroups: { name: string; value: number }[];
    languages: { name: string; value: number }[];
  }
  const [demographics, setDemographics] = useState<DemographicsData | null>(null);
  const [demLoading, setDemLoading] = useState(false);

  useEffect(() => { loadData(); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === 3 && !retention) loadRetention();
    if (tab === 4) loadReferrals();
    if (tab === 5) loadCoachStats();
    if (tab === 6 && !demographics) loadDemographics();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  function getDaysAgo(d: number) { return new Date(Date.now() - d * 86400000).toISOString(); }
  const daysMap: Record<Period, number> = { '7d': 7, '14d': 14, '30d': 30, '90d': 90 };

  async function loadData() {
    setLoading(true);
    await Promise.all([loadRevenue(), loadUsage(), loadFunnel()]);
    setLoading(false);
  }

  async function loadRevenue() {
    const since = getDaysAgo(daysMap[period]);
    const { data } = await supabase.from('payments').select('amount, product_price_id, product_prices:product_price_id(name)').eq('status', 'succeeded').gte('created_at', since);
    if (!data) return;

    const byProduct: Record<string, number> = {};
    data.forEach(p => {
      const name = (p.product_prices as unknown as { name?: string } | null)?.name ?? p.product_price_id;
      byProduct[name] = (byProduct[name] ?? 0) + (p.amount ?? 0);
    });
    setRevenue(Object.entries(byProduct).map(([name, valor]) => ({ name, valor })).sort((a, b) => b.valor - a.valor));

    const { data: subs } = await supabase.from('profiles').select('subscription_tier');
    const tiers: Record<string, number> = {};
    (subs ?? []).forEach(s => { tiers[s.subscription_tier] = (tiers[s.subscription_tier] ?? 0) + 1; });
    setPlans([
      { name: 'Grátis', value: tiers.free ?? 0 },
      { name: 'Mensal', value: tiers.monthly ?? 0 },
      { name: 'Anual', value: tiers.annual ?? 0 },
    ]);
  }

  async function loadUsage() {
    const since = getDaysAgo(daysMap[period]);
    const { data } = await supabase.from('reading_analytics').select('feature, feedback').gte('created_at', since);
    if (!data) return;
    const map: Record<string, { count: number; pos: number }> = {};
    data.forEach(r => {
      if (!map[r.feature]) map[r.feature] = { count: 0, pos: 0 };
      map[r.feature].count++;
      if (r.feedback === 'positive') map[r.feature].pos++;
    });
    setUsageData(Object.entries(map).map(([feature, v]) => ({
      feature, count: v.count, feedback: v.count ? Math.round((v.pos / v.count) * 100) : 0,
    })).sort((a, b) => b.count - a.count));
  }

  async function loadFunnel() {
    const since = getDaysAgo(daysMap[period]);
    const [{ count: _visits }, { count: reg }, { count: onboarded }, { count: readings }, { count: paid }] = await Promise.all([
      supabase.from('profiles').select('id', { count: 'exact', head: true }).gte('created_at', since),
      supabase.from('profiles').select('id', { count: 'exact', head: true }).gte('created_at', since),
      supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('onboarding_done', true).gte('created_at', since),
      supabase.from('reading_analytics').select('user_id', { count: 'exact', head: true }).gte('created_at', since),
      supabase.from('payments').select('user_id', { count: 'exact', head: true }).eq('status', 'succeeded').gte('created_at', since),
    ]);

    const steps = [
      { step: 'Visitaram homepage',     value: Math.round((reg ?? 0) * 3.2) },
      { step: 'Criaram conta',          value: reg ?? 0 },
      { step: 'Completaram onboarding', value: onboarded ?? 0 },
      { step: 'Fizeram 1ª leitura',     value: readings ?? 0 },
      { step: 'Tornaram-se pagantes',   value: paid ?? 0 },
    ];
    setFunnel(steps.map((s, i) => ({
      ...s,
      rate: i === 0 ? '100%' : `${s.value > 0 && steps[i-1].value > 0 ? Math.round((s.value / steps[i-1].value) * 100) : 0}%`,
    })));
  }

  async function loadRetention() {
    setRetentionLoading(true);
    const { data } = await supabase.functions.invoke('admin-retention-data', {});
    if (data?.data) setRetention(data.data);
    setRetentionLoading(false);
  }

  async function loadReferrals() {
    const { data } = await supabase.from('referrals').select('referral_code, status');
    if (!data) return;
    const map: Record<string, { count: number; converted: number }> = {};
    data.forEach(r => {
      const key = r.referral_code ?? 'direto';
      if (!map[key]) map[key] = { count: 0, converted: 0 };
      map[key].count++;
      if (r.status === 'converted') map[key].converted++;
    });
    setReferrals(Object.entries(map).map(([source, v]) => ({ source, count: v.count, converted: v.converted })).sort((a, b) => b.count - a.count).slice(0, 10));
  }

  async function loadDemographics() {
    setDemLoading(true);
    const { data } = await supabase.from('profiles').select('sun_sign, gender, birth_date, language').limit(5000);
    if (!data) { setDemLoading(false); return; }

    // Signs distribution
    const signMap: Record<string, number> = {};
    for (const p of data) { if (p.sun_sign) signMap[p.sun_sign] = (signMap[p.sun_sign] ?? 0) + 1; }
    const signs = Object.entries(signMap).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

    // Gender
    const genderMap: Record<string, number> = {};
    for (const p of data) {
      const g = p.gender ?? 'Não informado';
      genderMap[g] = (genderMap[g] ?? 0) + 1;
    }
    const genders = Object.entries(genderMap).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

    // Age groups
    const now = new Date();
    const ageGroupMap: Record<string, number> = { '<18': 0, '18–24': 0, '25–34': 0, '35–44': 0, '45–54': 0, '55–64': 0, '65+': 0, 'N/A': 0 };
    for (const p of data) {
      if (!p.birth_date) { ageGroupMap['N/A']++; continue; }
      const age = now.getFullYear() - new Date(p.birth_date).getFullYear();
      if (age < 18) ageGroupMap['<18']++;
      else if (age <= 24) ageGroupMap['18–24']++;
      else if (age <= 34) ageGroupMap['25–34']++;
      else if (age <= 44) ageGroupMap['35–44']++;
      else if (age <= 54) ageGroupMap['45–54']++;
      else if (age <= 64) ageGroupMap['55–64']++;
      else ageGroupMap['65+']++;
    }
    const ageGroups = Object.entries(ageGroupMap).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));

    // Languages
    const langMap: Record<string, number> = {};
    for (const p of data) { const l = p.language ?? 'pt'; langMap[l] = (langMap[l] ?? 0) + 1; }
    const languages = Object.entries(langMap).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

    setDemographics({ signs, genders, ageGroups, languages });
    setDemLoading(false);
  }

  async function loadCoachStats() {
    const [{ data: subs }, { data: results }] = await Promise.all([
      supabase.from('astro_coach_subscriptions').select('status, plan, weeks_completed'),
      supabase.from('astro_coach_results').select('user_id, week_number, status').eq('status', 'completed'),
    ]);
    const active = (subs ?? []).filter(s => s.status === 'active').length;
    const completed = (subs ?? []).filter(s => (s.weeks_completed ?? []).length >= 12).length;
    const totalWeeks = (subs ?? []).reduce((acc, s) => acc + ((s.weeks_completed ?? []).length), 0);
    const avgWeek = subs?.length ? Math.round(totalWeeks / subs.length) : 0;
    const byPlanMap: Record<string, number> = {};
    (subs ?? []).forEach(s => { const p = s.plan ?? 'annual'; byPlanMap[p] = (byPlanMap[p] ?? 0) + 1; });
    const byPlan = Object.entries(byPlanMap).map(([plan, count]) => ({ plan, count }));
    setCoachStats({ active, completed, avgWeek, byPlan });
    void results;
  }

  return (
    <div>
      <AdminPageHeader
        title="Analytics"
        objective="Análise de desempenho da plataforma: receita por produto, quais serviços são mais usados, como os usuários evoluem do cadastro até a compra (funil) e taxa de retenção mês a mês. Selecione o período nos botões acima."
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.35rem' }}>
          {PERIODS.map(p => (
            <button key={p.key} onClick={() => setPeriod(p.key)}
              className={period === p.key ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '1.5rem', overflowX: 'auto' }}>
        {TABS.map((t, i) => (
          <button key={t.label} onClick={() => setTab(i)}
            className={tab === i ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            style={{ whiteSpace: 'nowrap' }}
            title={t.title}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="card shimmer" style={{ height: '300px' }} /> : (
        <>
          {/* Tab 0: Receita */}
          {tab === 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: '1rem', alignItems: 'start' }}>
              <div className="card">
                <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem' }}>Receita por produto</p>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={revenue} layout="vertical">
                    <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} tickFormatter={v => formatBRL(v)} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} width={120} />
                    <Tooltip formatter={(v: number) => formatBRL(v)} contentStyle={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '6px', fontSize: '12px' }} />
                    <Bar dataKey="valor" fill="#F5C842" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="card">
                <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem' }}>Planos</p>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={plans} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                      {plans.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '6px', fontSize: '12px' }} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Tab 1: Uso por funcionalidade */}
          {tab === 1 && (
            <div className="card">
              <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem' }}>Leituras por funcionalidade</p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={usageData}>
                  <XAxis dataKey="feature" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '6px', fontSize: '12px' }} />
                  <Bar dataKey="count" name="Leituras" fill="#F5C842" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="feedback" name="Feedback positivo %" fill="#4ade80" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Tab 2: Funil */}
          {tab === 2 && (
            <div className="card">
              <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Funil de conversão</p>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '1.25rem', padding: '0.5rem 0.75rem', backgroundColor: 'rgba(96,165,250,0.08)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(96,165,250,0.15)' }}>
                ℹ️ Os dados do funil são estimativas baseadas nas ações registradas. Podem não refletir todos os acessos.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {funnel.map((step, i) => (
                  <div key={step.step}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <span style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>{step.step}</span>
                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <span style={{ fontSize: 'var(--text-label)', color: 'var(--text-primary)', fontWeight: 600 }}>{step.value.toLocaleString('pt-BR')}</span>
                        <span style={{ fontSize: 'var(--text-label)', color: i === 0 ? 'var(--text-muted)' : 'var(--petrol)', width: '45px', textAlign: 'right' }}>{step.rate}</span>
                      </div>
                    </div>
                    <div style={{ height: '8px', backgroundColor: 'var(--bg-base)', borderRadius: 'var(--radius-full)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${funnel[0].value > 0 ? (step.value / funnel[0].value) * 100 : 0}%`, backgroundColor: '#F5C842', borderRadius: 'var(--radius-full)', opacity: 1 - i * 0.12 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tab 3: Retenção */}
          {tab === 3 && (
            <div className="card">
              <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Retenção por coorte semanal</p>
              {retentionLoading ? (
                <div className="shimmer" style={{ height: '180px', borderRadius: 'var(--radius-md)' }} />
              ) : retention?.insufficient ? (
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-label)' }}>⏳ {retention.reason ?? 'Dados insuficientes para análise de retenção.'}</p>
                </div>
              ) : (
                <>
                  <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '1rem', padding: '0.5rem 0.75rem', backgroundColor: 'rgba(96,165,250,0.08)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(96,165,250,0.15)' }}>
                    Coorte = grupo de usuários que se cadastraram na mesma semana. W1 = % que voltaram na 1ª semana, W4 = 4ª semana, M3 = 3º mês.
                  </p>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="data-table" style={{ width: '100%' }}>
                      <thead>
                        <tr><th>Coorte</th><th style={{ textAlign: 'center' }}>Usuários</th><th style={{ textAlign: 'center' }}>Semana 1</th><th style={{ textAlign: 'center' }}>Semana 4</th><th style={{ textAlign: 'center' }}>Mês 3</th></tr>
                      </thead>
                      <tbody>
                        {(retention?.cohorts ?? []).map(row => (
                          <tr key={row.cohort}>
                            <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{row.cohort}</td>
                            <td style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>{row.users}</td>
                            <td style={{ textAlign: 'center' }}>
                              {row.week1 !== null ? (
                                <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '12px', backgroundColor: row.week1 >= 50 ? 'rgba(74,222,128,0.15)' : row.week1 >= 25 ? 'rgba(251,191,36,0.15)' : 'rgba(239,68,68,0.1)', color: row.week1 >= 50 ? '#4ade80' : row.week1 >= 25 ? '#fbbf24' : '#f87171' }}>{row.week1}%</span>
                              ) : <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>-</span>}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {row.week4 !== null ? (
                                <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '12px', backgroundColor: row.week4 >= 30 ? 'rgba(74,222,128,0.15)' : row.week4 >= 15 ? 'rgba(251,191,36,0.15)' : 'rgba(239,68,68,0.1)', color: row.week4 >= 30 ? '#4ade80' : row.week4 >= 15 ? '#fbbf24' : '#f87171' }}>{row.week4}%</span>
                              ) : <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>-</span>}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {row.month3 !== null ? (
                                <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '12px', backgroundColor: row.month3 >= 20 ? 'rgba(74,222,128,0.15)' : row.month3 >= 10 ? 'rgba(251,191,36,0.15)' : 'rgba(239,68,68,0.1)', color: row.month3 >= 20 ? '#4ade80' : row.month3 >= 10 ? '#fbbf24' : '#f87171' }}>{row.month3}%</span>
                              ) : <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>-</span>}
                            </td>
                          </tr>
                        ))}
                        {(!retention?.cohorts?.length) && (
                          <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '1.5rem' }}>Nenhum dado disponível</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Tab 4: Aquisição */}
          {tab === 4 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div className="card">
                <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>Indicações (referral_code)</p>
                <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                  Rastreamento UTM não implementado. Os dados abaixo mostram indicações via código.
                </p>
                {referrals.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 'var(--text-label)' }}>Nenhuma indicação registrada.</p>
                ) : (
                  <table className="data-table" style={{ width: '100%' }}>
                    <thead><tr><th>Código</th><th>Indicados</th><th>Convertidos</th><th>Taxa</th></tr></thead>
                    <tbody>
                      {referrals.map(r => (
                        <tr key={r.source}>
                          <td style={{ color: 'var(--text-secondary)', fontFamily: 'monospace', fontSize: '12px' }}>{r.source}</td>
                          <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{r.count}</td>
                          <td style={{ color: '#4ade80', fontWeight: 600 }}>{r.converted}</td>
                          <td><span style={{ color: r.count > 0 && (r.converted / r.count) > 0.15 ? '#4ade80' : '#fbbf24', fontWeight: 600 }}>{r.count > 0 ? Math.round((r.converted / r.count) * 100) : 0}%</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Tab 5: Astro Coach */}
          {tab === 5 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {!coachStats ? (
                <div className="card shimmer" style={{ height: '200px' }} />
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                    {[
                      { label: 'Assinaturas ativas', value: coachStats.active, color: '#4ade80' },
                      { label: 'Completaram 12 semanas', value: coachStats.completed, color: '#c084fc' },
                      { label: 'Média de semanas concluídas', value: coachStats.avgWeek, color: '#60a5fa' },
                    ].map(m => (
                      <div key={m.label} className="card" style={{ textAlign: 'center' }}>
                        <p style={{ fontSize: '2rem', fontWeight: 700, color: m.color, margin: 0 }}>{m.value}</p>
                        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '0.25rem' }}>{m.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="card">
                    <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem' }}>Assinaturas por plano</p>
                    {coachStats.byPlan.length === 0 ? (
                      <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Nenhuma assinatura.</p>
                    ) : (
                      <table className="data-table" style={{ width: '100%' }}>
                        <thead><tr><th>Plano</th><th>Assinaturas</th></tr></thead>
                        <tbody>
                          {coachStats.byPlan.map(b => (
                            <tr key={b.plan}>
                              <td style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{b.plan}</td>
                              <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{b.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          {/* Tab 6: Demographics */}
          {tab === 6 && (
            demLoading ? <div className="card shimmer" style={{ height: '300px' }} /> : !demographics ? (
              <p style={{ color: 'var(--text-muted)' }}>Carregando dados demográficos…</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem' }}>
                {/* Signs */}
                <div className="card">
                  <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem' }}>Signos Solares</p>
                  {demographics.signs.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>Sem dados</p> : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      {demographics.signs.slice(0, 12).map(s => {
                        const max = demographics.signs[0].value;
                        return (
                          <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', width: '80px', flexShrink: 0 }}>{s.name}</span>
                            <div style={{ flex: 1, height: '8px', borderRadius: '4px', background: 'var(--bg-elevated)', overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${(s.value / max) * 100}%`, background: 'var(--petrol)', borderRadius: '4px' }} />
                            </div>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)', width: '32px', textAlign: 'right' }}>{s.value}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Gender */}
                <div className="card">
                  <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem' }}>Gênero</p>
                  {demographics.genders.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>Sem dados</p> : (
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie data={demographics.genders} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                          {demographics.genders.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '6px', fontSize: '12px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>

                {/* Age groups */}
                <div className="card">
                  <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem' }}>Faixa Etária</p>
                  {demographics.ageGroups.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>Sem dados</p> : (
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={demographics.ageGroups} layout="vertical">
                        <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} width={50} />
                        <Tooltip contentStyle={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '6px', fontSize: '12px' }} />
                        <Bar dataKey="value" fill="#a78bfa" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>

                {/* Languages */}
                <div className="card">
                  <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem' }}>Idioma da Conta</p>
                  {demographics.languages.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>Sem dados</p> : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      {demographics.languages.map(l => (
                        <div key={l.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-label)', padding: '0.25rem 0', borderBottom: '1px solid var(--border-subtle)' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>{l.name}</span>
                          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{l.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <p style={{ marginTop: '1rem', fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    Nota: localização por IP requer integração com serviço externo de geolocalização (ex: MaxMind, ipinfo.io). Os dados acima são baseados no idioma configurado na conta.
                  </p>
                </div>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
